import { useEffect, useState } from "react";
import { GraduationCap, HeartPulse, Train, ShoppingBag, MapPin, Loader2 } from "lucide-react";

/** Nearby Points of Interest widget powered by the FREE OpenStreetMap Overpass API.
 *  Renders 4 categories — Schools, Hospitals, Metro/Rail, Shopping — with the closest 4 within a 2km radius.
 *  No API key required. Overpass endpoint is public and CORS-friendly. */

const CATEGORIES = [
    { key: "school", label: "Schools", icon: GraduationCap, filter: '["amenity"~"school|college|university|kindergarten"]', color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { key: "hospital", label: "Hospitals", icon: HeartPulse, filter: '["amenity"~"hospital|clinic|doctors|pharmacy"]', color: "text-rose-700 bg-rose-50 border-rose-200" },
    { key: "transit", label: "Metro / Rail", icon: Train, filter: '["railway"~"station|subway_entrance|halt|tram_stop"]', color: "text-blue-700 bg-blue-50 border-blue-200" },
    { key: "market", label: "Shopping", icon: ShoppingBag, filter: '["shop"~"mall|supermarket|convenience"]', color: "text-violet-700 bg-violet-50 border-violet-200" },
];

const OVERPASS_URL = "https://overpass-api.de/api/interpreter";
const RADIUS_M = 2000; // 2km search radius

// Haversine distance (metres) between two [lat, lon] points
function distance(a, b) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLon = toRad(b[1] - a[1]);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
}

const fmtDist = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

export default function NearbyPOIs({ lat, lng }) {
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        if (lat == null || lng == null) { setLoading(false); return; }
        let cancelled = false;

        const query = `[out:json][timeout:20];\n(\n${CATEGORIES.map((c) => `  node${c.filter}(around:${RADIUS_M},${lat},${lng});`).join("\n")}\n);\nout tags center 60;`;

        (async () => {
            try {
                const res = await fetch(OVERPASS_URL, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: "data=" + encodeURIComponent(query),
                });
                const data = await res.json();
                if (cancelled) return;
                const grouped = {};
                CATEGORIES.forEach((c) => { grouped[c.key] = []; });
                (data.elements || []).forEach((el) => {
                    const tags = el.tags || {};
                    // Classify element into one of our categories
                    let key = null;
                    if (tags.amenity && /school|college|university|kindergarten/.test(tags.amenity)) key = "school";
                    else if (tags.amenity && /hospital|clinic|doctors|pharmacy/.test(tags.amenity)) key = "hospital";
                    else if (tags.railway && /station|subway_entrance|halt|tram_stop/.test(tags.railway)) key = "transit";
                    else if (tags.shop && /mall|supermarket|convenience/.test(tags.shop)) key = "market";
                    if (!key) return;
                    const name = tags.name || tags["name:en"] || (tags.amenity || tags.railway || tags.shop || "").replace(/_/g, " ");
                    const p = [el.lat || el.center?.lat, el.lon || el.center?.lon];
                    if (p[0] == null || p[1] == null) return;
                    grouped[key].push({ name, distance: distance([lat, lng], p) });
                });
                CATEGORIES.forEach((c) => {
                    grouped[c.key] = grouped[c.key].sort((a, b) => a.distance - b.distance).slice(0, 4);
                });
                setResults(grouped);
            } catch (e) {
                if (!cancelled) setErrored(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [lat, lng]);

    if (lat == null || lng == null) return null;

    return (
        <div className="rounded-2xl bg-white border border-navy-500/10 p-5 sm:p-6" data-testid="nearby-pois">
            <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-gold-600" />
                <h3 className="font-heading font-bold text-navy-500 text-lg">What&apos;s nearby</h3>
                <span className="text-[10px] text-navy-500/50 uppercase tracking-wider">within 2 km</span>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-navy-500/60 text-sm py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-gold-500" /> Fetching nearby amenities…
                </div>
            ) : errored ? (
                <div className="text-navy-500/60 text-sm py-4">Nearby data unavailable at the moment. Please try again later.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const list = results[c.key] || [];
                        return (
                            <div key={c.key} data-testid={`poi-${c.key}`}>
                                <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${c.color} mb-2`}>
                                    <Icon className="w-3.5 h-3.5" /> {c.label}
                                </div>
                                {list.length === 0 ? (
                                    <div className="text-xs text-navy-500/50">No {c.label.toLowerCase()} within 2 km</div>
                                ) : (
                                    <ul className="space-y-1">
                                        {list.map((p, i) => (
                                            <li key={i} className="flex items-center justify-between gap-3 text-sm">
                                                <span className="text-navy-500/85 line-clamp-1 capitalize">{p.name}</span>
                                                <span className="shrink-0 text-[11px] font-semibold text-gold-700">{fmtDist(p.distance)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-4 pt-3 border-t border-navy-500/10 text-[10px] text-navy-500/40">
                Data via OpenStreetMap contributors · updated in real time
            </div>
        </div>
    );
}
